/**
 * @file Queue.jsx
 * @description Queue component for displaying and managing queue table.
 * @component Queue
 * @exports Queue - Main component for displaying and managing queue table.
 */

import React, { useState, useEffect, useMemo } from "react";
import Grid from "@mui/material/Grid2";
import { Add } from "@mui/icons-material";
import { TextField, Button, Alert } from "@mui/material";
import { POLICY_ERRORS } from "../../../common/constants/ivrModule/policyConstants.js";
import { AUTO_COMPLETE, VARIANTS, BUTTON_COLORS, STATUS, SORT_ORDERS, SIZES } from "../../../common/constants/commonConstants.js";
import { QUEUE_STRINGS, QUEUE_KEYS, QUEUE_MESSAGES, QUEUE_ERRORS, QUEUE_TABLE_COLUMNS } from "../../../common/constants/ivrModule/queueConstants.js";
import { getComparator, handleSort } from "../../../hooks/useTable.js";
import { getIvrDistributionPolicy } from "../../../services/ivrModule/policyServices.js";
import { getIvrQueue, addEditIvrQueue, deleteIvrQueue } from "../../../services/ivrModule/queueServices.js";
import QueueTable from "./QueueTable.jsx";
import AddEditQueueDialog from "./AddEditQueueDialog.jsx";
import DeleteQueueDialog from "./DeleteQueueDialog.jsx";

const Queue = () => {
    const [page, setPage] = useState(0);
    const [ivrQueue, setIvrQueue] = useState([]);
    const [ivrPolicies, setIvrPolicies] = useState([]);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [messageType, setMessageType] = useState("");
    const [isEditQueue, setIsEditQueue] = useState(false);
    const [loadingQueue, setLoadingQueue] = useState(true);
    const [selectedQueue, setSelectedQueue] = useState(null);
    const [successMessage, setSuccessMessage] = useState("");
    const [searchIvrQueue, setSearchIvrQueue] = useState("");
    const [order, setOrder] = useState(SORT_ORDERS.ASCENDING);
    const [loadingPolicies, setLoadingPolicies] = useState(true);
    const [orderBy, setOrderBy] = useState(QUEUE_KEYS.QUEUE_NAME);
    const [isDeleteQueueModalOpen, setIsDeleteQueueModalOpen] = useState(false);
    const [isAddEditQueueModalOpen, setIsAddEditQueueModalOpen] = useState(false);

    useEffect(() => {
        fetchQueue();
        fetchPolicies();
    }, []);
    
    const fetchQueue = async () => {
        try {
            setLoadingQueue(true);
            setIvrQueue(await getIvrQueue());
        }
        catch (error) {
            console.error(QUEUE_ERRORS.FETCH_QUEUE_ERROR, error);
        }
        finally {
            setLoadingQueue(false);
        }
    };

    const fetchPolicies = async () => {
        try {
            setLoadingPolicies(true);
            const policies = await getIvrDistributionPolicy();
            const policyIds = policies.map(policy => ({
                id: policy.id,
                label: policy.id
            }));
            setIvrPolicies(policyIds);
        }
        catch (error) {
            console.error(POLICY_ERRORS.FETCH_POLICY_ERROR, error);
        }
        finally {
            setLoadingPolicies(false);
        }
    };

    const { queueToDisplay, filteredQueueLength } = useMemo(() => {
        const filteredQueue = ivrQueue.filter((ivrQueue) =>
            ivrQueue.queuename.trim().toLowerCase().includes(searchIvrQueue.toLowerCase())
        );
        return {
            queueToDisplay: filteredQueue
                .sort(getComparator(order, orderBy))
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
            filteredQueueLength: filteredQueue.length,
        };
    }, [ivrQueue, searchIvrQueue, order, orderBy, page, rowsPerPage]);

    const handleAddEditQueueClick = (queue = {}) => {
        setIsEditQueue(!!queue.id);
        setSelectedQueue(queue);
        setIsAddEditQueueModalOpen(true);
    };

    const handleDeleteQueueClick = (queue = {}) => {
        setSelectedQueue(queue);
        setIsDeleteQueueModalOpen(true);
    };

    const handleSaveQueue = async () => {
        if (!selectedQueue) return;
        const queueToSave = {
            id: selectedQueue.id || "",
            queuename: selectedQueue.queuename || "",
            distributionPolicyId: selectedQueue?.distributionPolicyId || "",
        };
        try {
            await addEditIvrQueue(queueToSave);
            setIsAddEditQueueModalOpen(false);
            setSuccessMessage(isEditQueue ? QUEUE_MESSAGES.QUEUE_UPDATED : QUEUE_MESSAGES.QUEUE_ADDED);
            await fetchQueue();
            setTimeout(() => setSuccessMessage(""), 3000);
        } catch (error) { console.error(QUEUE_ERRORS.SAVE_QUEUE_ERROR, error); }
    };

    const handleDeleteQueue = async () => {
        if (!selectedQueue) return;
        const queueToDelete = { id: selectedQueue.id || "" };
        try {
            const res = await deleteIvrQueue(queueToDelete);
            setIsDeleteQueueModalOpen(false);
            if (res.error?.toLowerCase().includes("assigned worker")) {
                setSuccessMessage("Failed to Delete. There is an assigned worker to this queue");
                setMessageType(STATUS.ERROR);
            } else {
                setSuccessMessage(res.message || QUEUE_MESSAGES.QUEUE_DELETED);
                setMessageType(STATUS.SUCCESS);
                await fetchQueue();
            }
        } catch (error) {
            setIsDeleteQueueModalOpen(false);
            setSuccessMessage(error.message || "Failed to delete queue.");
        } finally {
            setTimeout(() => setSuccessMessage(""), 3000);
        }
    };

    const handleInputChange = (key) => (e) => {
        let value = e.target.value;
        if (key === QUEUE_KEYS.ID) {
            value = value.replace(/\s/g, "");
            value = `qu-${value.toLowerCase()}`;
        }
        setSelectedQueue((prev) => ({
            ...prev,
            [key]: value,
        }));
    };

    const handleRequestSort = (property) => {
        handleSort(order, orderBy, property, setOrder, setOrderBy);
    };

    return (
        <>
            {successMessage && 
                <Alert
                    severity={messageType === STATUS.SUCCESS ? STATUS.SUCCESS : STATUS.ERROR}
                    sx={{ mb: 2, justifyContent: "center" }}
                >
                    {successMessage}
                </Alert>
            }
            <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid size={SIZES.GROW}>
                    <TextField
                        fullWidth
                        size={SIZES.SMALL}
                        value={searchIvrQueue}
                        variant={VARIANTS.OUTLINED}
                        autoComplete={AUTO_COMPLETE.OFF}
                        label={QUEUE_STRINGS.SEARCH_QUEUE}
                        onChange={e => setSearchIvrQueue(e.target.value)}
                    />
                </Grid>
                <Grid size={SIZES.AUTO}>
                    <Button
                        startIcon={<Add />}
                        variant={VARIANTS.CONTAINED}
                        color={BUTTON_COLORS.PRIMARY}
                        onClick={() => handleAddEditQueueClick({})}
                    >
                        {QUEUE_STRINGS.ADD_QUEUE}
                    </Button>
                </Grid>
            </Grid>
            <QueueTable
                page={page}
                order={order}
                setPage={setPage}
                orderBy={orderBy}
                queue={queueToDisplay}
                rowsPerPage={rowsPerPage}
                columns={QUEUE_TABLE_COLUMNS}
                isQueueLoading={loadingQueue || loadingPolicies}
                setRowsPerPage={setRowsPerPage}
                onEdit={handleAddEditQueueClick}
                onDelete={handleDeleteQueueClick}
                handleRequestSort={handleRequestSort}
                filteredQueueLength={filteredQueueLength}
            />
            <AddEditQueueDialog
                onSave={handleSaveQueue}
                isEditQueue={isEditQueue}
                ivrPolicies={ivrPolicies}
                selectedQueue={selectedQueue}
                open={isAddEditQueueModalOpen}
                handleInputChange={handleInputChange}
                onClose={() => setIsAddEditQueueModalOpen(false)}
            />
            <DeleteQueueDialog
                onDelete={handleDeleteQueue}
                selectedQueue={selectedQueue}
                open={isDeleteQueueModalOpen}
                onClose={() => setIsDeleteQueueModalOpen(false)}
            />
        </>
    );
};

export default Queue;